import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
	Calendar as AriaCalendar,
	CalendarGridHeader as AriaCalendarGridHeader,
	type CalendarProps as AriaCalendarProps,
	CalendarCell,
	CalendarGrid,
	CalendarGridBody,
	CalendarHeaderCell,
	type DateValue,
	Text
} from 'react-aria-components';
import { Button } from './buttons/Button';
import { MonthYearDropdowns } from './MonthYearDropdowns';
import { cn, focusRing } from './utils';

export interface CalendarProps<T extends DateValue> extends Omit<AriaCalendarProps<T>, 'visibleDuration'> {
	errorMessage?: string;
}

export function Calendar<T extends DateValue>({ errorMessage, ...props }: CalendarProps<T>) {
	return (
		<AriaCalendar {...props} className="rounded-lg bg-white p-2 shadow">
			<CalendarHeader />
			<CalendarGrid className="w-full">
				<CalendarGridHeader />
				<CalendarGridBody>
					{date => (
						<CalendarCell
							date={date}
							className={renderProps =>
								cn(
									'flex h-9 w-9 cursor-default items-center justify-center rounded-full text-sm forced-color-adjust-none',
									focusRing(renderProps),
									renderProps.isSelected
										? 'bg-primary-600 text-white invalid:bg-red-600 forced-colors:bg-[Highlight] forced-colors:text-[HighlightText] forced-colors:invalid:bg-[Mark]'
										: 'pressed:bg-gray-200 text-zinc-900 hover:bg-gray-100',
									renderProps.isDisabled && 'text-gray-300 forced-colors:text-[GrayText]'
								)
							}
						/>
					)}
				</CalendarGridBody>
			</CalendarGrid>
			{errorMessage && (
				<Text slot="errorMessage" className="text-sm text-red-600">
					{errorMessage}
				</Text>
			)}
		</AriaCalendar>
	);
}

export function CalendarHeader() {
	return (
		<header className="flex items-center justify-between gap-1 px-1 pb-4">
			<Button Icon={ChevronLeft} slot="previous" appearance="outline" intent="neutralLight" />
			<div className="flex min-w-0 flex-1 items-center gap-2">
				<MonthYearDropdowns />
			</div>

			{/* <Heading className="mx-2 flex-1 text-center text-xl font-semibold text-zinc-900" /> */}
			<Button Icon={ChevronRight} slot="next" appearance="outline" intent="neutralLight" />
		</header>
	);
}

export function CalendarGridHeader() {
	return (
		<AriaCalendarGridHeader>
			{day => <CalendarHeaderCell className="text-xs font-semibold text-gray-500">{day}</CalendarHeaderCell>}
		</AriaCalendarGridHeader>
	);
}
